
'use client';

import { useState } from 'react';
import type { Birthday } from '@/lib/types';
import {
  calculateAge,
  getDayOfWeek,
  getNextBirthday,
  formatDate,
  formatTime,
} from '@/lib/date-utils';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useBirthdays } from '@/hooks/use-birthdays';
import { GiftSuggestion } from './gift-suggestion';
import { BirthdayForm } from './birthday-form';
import {
  Cake,
  Calendar,
  Gift,
  Trash2,
  Users,
  StickyNote,
  CalendarClock,
  Bell,
  Pencil,
  Clock,
  ChevronDown,
} from 'lucide-react';
import { differenceInDays, isToday, subDays } from 'date-fns';
import { useNotifications } from '@/hooks/use-notifications';
import { useToast } from '@/hooks/use-toast';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

export function BirthdayCard({ birthday }: { birthday: Birthday }) {
  const { deleteBirthday } = useBirthdays();
  const { scheduleNotification } = useNotifications();
  const { toast } = useToast();
  const [showGiftSuggestion, setShowGiftSuggestion] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  const age = birthday.dob ? calculateAge(birthday.dob) : null;
  const nextBirthdayDate = birthday.dob ? getNextBirthday(birthday.dob) : null;
  const nextBirthdayDay = nextBirthdayDate ? getDayOfWeek(nextBirthdayDate) : null;
  const turning = age !== null ? age + 1 : null;
  const isBirthdayToday = nextBirthdayDate ? isToday(nextBirthdayDate) : false;

  const daysUntilNext = nextBirthdayDate
    ? Math.ceil(differenceInDays(nextBirthdayDate, new Date()))
    : null;

  const countdownText = () => {
    if (daysUntilNext === null) return '';
    if (daysUntilNext === 0) return 'today!';
    if (daysUntilNext === 1) return 'in 1 day';
    return `in ${daysUntilNext} days`;
  };

  const handleSetReminder = (option: 'on-day' | 'day-before' | 'week-before') => {
    if (!nextBirthdayDate || turning === null) {
      toast({
        variant: 'destructive',
        title: 'Cannot Set Reminder',
        description: 'A valid date of birth is required to set a reminder.',
      });
      return;
    }

    let reminderDate = nextBirthdayDate;
    let reminderDescription = `You'll be notified on ${birthday.name}'s birthday.`;

    switch (option) {
      case 'day-before':
        reminderDate = subDays(nextBirthdayDate, 1);
        reminderDescription = `You'll be notified one day before ${birthday.name}'s birthday.`;
        break;
      case 'week-before':
        reminderDate = subDays(nextBirthdayDate, 7);
        reminderDescription = `You'll be notified one week before ${birthday.name}'s birthday.`;
        break;
    }

    scheduleNotification(
      reminderDate,
      `Reminder: ${birthday.name}'s birthday is coming up!`,
      `They are turning ${turning}. Don't forget to wish them a happy birthday!`
    );
    toast({
      title: 'Reminder Set!',
      description: reminderDescription,
    });
  };

  return (
    <>
      <Card className="flex flex-col">
        <CardHeader>
          <div className="flex items-start justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                {birthday.name}
                {isBirthdayToday && (
                  <span className="text-2xl" role="img" aria-label="Party Popper">
                    🎉
                  </span>
                )}
              </CardTitle>
              {turning !== null && (
                <CardDescription>
                  Turning <span className="font-semibold">{turning}</span>{' '}
                  {countdownText()}
                </CardDescription>
              )}
            </div>
            {nextBirthdayDate && (
              <Badge variant={isBirthdayToday ? 'default' : 'secondary'}>
                {formatDate(nextBirthdayDate)}
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent className="flex-grow space-y-3">
          {birthday.dob && (
            <>
              <div className="flex items-center text-sm text-muted-foreground">
                <Cake className="mr-2 h-4 w-4" />
                <span>
                  Born on {formatDate(birthday.dob)} (a {getDayOfWeek(birthday.dob)})
                </span>
              </div>
              {birthday.time && (
                <div className="flex items-center text-sm text-muted-foreground">
                  <Clock className="mr-2 h-4 w-4" />
                  <span>{formatTime(birthday.time)}</span>
                </div>
              )}
              {nextBirthdayDay && (
                <div className="flex items-center text-sm text-muted-foreground">
                  <CalendarClock className="mr-2 h-4 w-4" />
                  <span>Next birthday is on a {nextBirthdayDay}</span>
                </div>
              )}
            </>
          )}
          <div className="flex items-center text-sm text-muted-foreground">
            <Users className="mr-2 h-4 w-4" />
            <span>Relation: {birthday.relation}</span>
          </div>
          {birthday.notes && (
            <div className="flex items-start text-sm text-muted-foreground">
              <StickyNote className="mr-2 h-4 w-4 flex-shrink-0 mt-1" />
              <p className="border-l-2 pl-3">{birthday.notes}</p>
            </div>
          )}
        </CardContent>
        <CardFooter className="flex justify-end gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" disabled={!nextBirthdayDate}>
                <Bell className="mr-2 h-4 w-4" />
                Set Reminder
                <ChevronDown className="ml-2 h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem onClick={() => handleSetReminder('on-day')}>
                On the day
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleSetReminder('day-before')}>
                1 day before
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleSetReminder('week-before')}>
                1 week before
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowGiftSuggestion(true)}
            disabled={!birthday.dob}
          >
            <Gift className="mr-2 h-4 w-4" />
            Suggest Gift
          </Button>
          <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" size="sm">
                <Pencil className="mr-2 h-4 w-4" />
                Edit
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Edit Birthday</DialogTitle>
                <DialogDescription>
                  Update the details for {birthday.name}.
                </DialogDescription>
              </DialogHeader>
              <BirthdayForm
                birthday={birthday}
                onSuccess={() => setIsEditModalOpen(false)}
              />
            </DialogContent>
          </Dialog>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="destructive" size="sm">
                <Trash2 className="mr-2 h-4 w-4" />
                Delete
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Are you sure?</AlertDialogTitle>
                <AlertDialogDescription>
                  This action cannot be undone. This will permanently delete the
                  birthday for {birthday.name}.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={() => deleteBirthday(birthday.id)}>
                  Delete
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </CardFooter>
      </Card>
      {showGiftSuggestion && (
        <GiftSuggestion
          isOpen={showGiftSuggestion}
          setIsOpen={setShowGiftSuggestion}
          birthday={birthday}
        />
      )}
    </>
  );
}
